import type { ReactNode } from 'react'
import cx from 'classnames'
import { Button, RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { ControlSizeContext } from '@/components/inputs/control-size'
import styles from './LegendEditor.module.css'

export interface LegendEditorProps {
  header: ReactNode
  /**
   * Shown under the header, like the banner for older versions.
   */
  banner?: ReactNode
  /**
   * The current tab.
   */
  children?: ReactNode
  /**
   * The editor's dialogs and the element palette.
   */
  dialogs?: ReactNode
  testId?: string
}

/**
 * The legend editor: a header over the current tab, filling the page. Its
 * controls are compact, so that the canvas and the inspector fit more.
 */
export function LegendEditor({
  header,
  banner,
  children,
  dialogs,
  testId,
}: LegendEditorProps) {
  return (
    <div className={styles.root} data-test={testId}>
      <ControlSizeContext value="compact">
        {header}
        {banner}
        <div className={styles.body}>{children}</div>
      </ControlSizeContext>
      {dialogs}
    </div>
  )
}

export interface LegendEditorHeaderProps {
  /**
   * Where the legend sits: the breadcrumb to the legend list.
   */
  breadcrumb: ReactNode
  /**
   * The version picker and the discard button.
   */
  versionControls: ReactNode
  /**
   * The editor's tabs.
   */
  tabs: ReactNode
  /**
   * The save status, the palette button and the primary action.
   */
  actions: ReactNode
  isViewingOlderVersion: boolean
}

/**
 * The editor's header: where you are, the version, the tabs and publishing.
 */
export function LegendEditorHeader({
  breadcrumb,
  versionControls,
  tabs,
  actions,
  isViewingOlderVersion,
}: LegendEditorHeaderProps) {
  return (
    <header
      className={cx(
        styles.header,
        isViewingOlderVersion && styles.olderVersion,
      )}
    >
      <div className={styles.headerStart}>
        {breadcrumb}
        {versionControls}
      </div>
      <nav aria-label="Legend editor">{tabs}</nav>
      <div className={styles.headerEnd}>{actions}</div>
    </header>
  )
}

export interface SaveStatusProps {
  /**
   * What happened last, like "Saved 2 minutes ago".
   */
  text: string
  isSaving: boolean
  /**
   * Why the save took as long as it did.
   */
  title?: string
}

/**
 * Whether the latest changes are saved.
 */
export function SaveStatus({ text, isSaving, title }: SaveStatusProps) {
  return (
    <span className={styles.saveStatus} title={title}>
      {isSaving ? (
        'Saving…'
      ) : (
        <>
          <Icon name="checkmark" size={13} className={styles.savedIcon} />
          {text}
        </>
      )}
    </span>
  )
}

export interface PaletteButtonProps {
  shortcutLabel: string
  onPress: () => unknown
  testId?: string
}

/**
 * Opens the palette that jumps to any element.
 */
export function PaletteButton({
  shortcutLabel,
  onPress,
  testId,
}: PaletteButtonProps) {
  return (
    <RawButton
      className={styles.palette}
      onPress={onPress}
      title="Jump to an element"
      testId={testId}
    >
      <Icon name="search" size={16} />
      <span>Jump to…</span>
      <kbd className={styles.shortcut}>{shortcutLabel}</kbd>
    </RawButton>
  )
}

export interface OlderVersionBannerProps {
  version: number
  isLive: boolean
  isPending: boolean
  onShowLatest: () => unknown
}

/**
 * Says that older versions are read-only, and offers the way back.
 */
export function OlderVersionBanner({
  version,
  isLive,
  isPending,
  onShowLatest,
}: OlderVersionBannerProps) {
  return (
    <div className={styles.banner}>
      <Icon name="history" size={15} />
      <span className={styles.bannerText}>
        You're viewing <strong>V{version}</strong>
        {isLive ? ', the version clients see. ' : ', an older version. '}
        It's read-only.
      </span>
      <Button
        label="Back to latest"
        size="small"
        isPending={isPending}
        onPress={onShowLatest}
      />
    </div>
  )
}

export interface HydrationErrorBannerProps {
  error: string
}

/**
 * Says that the version is read-only because part of it failed to load, so
 * that saving can't replace that part.
 */
export function HydrationErrorBanner({ error }: HydrationErrorBannerProps) {
  return (
    <div className={styles.banner} role="alert">
      <Icon name="status-alert" size={15} />
      <span className={styles.bannerText}>
        Part of this legend failed to load, so it's read-only. {error}
      </span>
    </div>
  )
}
